import React, { useState, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { withStyles } from '@mui/styles'
import { FormControl, Select, MenuItem, ListItemText } from '@mui/material'

import { Checkbox, CircularProgress } from '@mui/material'

import { filterInputStyles } from './filter-input.styles'

/**
 * @typedef {object} FilterInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {Array<{key: string|number, value: string}>} [values=[]] - Array de opciones disponibles.
 * @property {boolean} [multiple=false] - Si es true, permite selección múltiple con checkboxes.
 * @property {string} [defaultFilter] - Texto para la opción "Todos" (solo modo simple).
 * @property {object} [MenuProps] - Props adicionales para el componente Menu del Select.
 * @property {string} name - Nombre del campo.
 * @property {string|Array<string>} value - Valor(es) seleccionado(s). En múltiple, string separado por comas.
 * @property {function(event):void} onChange - Función invocada al cambiar la selección.
 * @property {string} label - Etiqueta mostrada antes del valor seleccionado.
 * @property {boolean} [required] - Si es true, marca el campo como requerido.
 * @property {boolean} [disabled] - Si es true, deshabilita el campo.
 */

/**
 * Select de filtrado con soporte para selección simple o múltiple.
 *
 * Componente select estilizado para filtros con soporte para selección múltiple (checkboxes),
 * opción "Todos" en modo simple, renderizado personalizado de valores seleccionados, y estado
 * de carga. En modo múltiple, los valores se gestionan como array y se convierten a/desde
 * string separado por comas.
 *
 * @component
 * @example
 * // Modo simple
 * <FilterInput
 *   name="status"
 *   label="Estado"
 *   value="active"
 *   values={[{ key: 'active', value: 'Activo' }, { key: 'inactive', value: 'Inactivo' }]}
 *   onChange={(e) => console.log(e.target.value)}
 * />
 *
 * // Modo múltiple
 * <FilterInput
 *   name="categories"
 *   label="Categorías"
 *   value="1,2,3"
 *   multiple={true}
 *   values={[{ key: '1', value: 'Cat 1' }, { key: '2', value: 'Cat 2' }]}
 *   onChange={(e) => console.log(e.target.value)}
 * />
 *
 * @param {FilterInputProps} props - Las props del componente FilterInput.
 */
export const FilterInput = withStyles(filterInputStyles)(({
  classes,
  values = [],
  multiple = false,
  defaultFilter,
  MenuProps,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const value = useMemo(
    () => (multiple ? (props.value ? props.value.split(',') : []) : props.value || ''),
    [multiple, props.value]
  )

  const defaultFilterValue = defaultFilter ? defaultFilter : formatMessage({ id: 'global.all' })

  const [selectedValues, setSelectedValues] = useState([])

  useEffect(() => {
    setSelectedValues(value)
  }, [value])

  const handleChange = (e) => {
    if (multiple) {
      setSelectedValues(e.target.value)
    } else props.onChange(e)
  }

  if (!values || values.length === 0) {
    return null
  }

  const getSelectOptions = () => {
    const options = values.map((item, i) => (
      <MenuItem key={i} value={item.key}>
        {multiple ? (
          <>
            <Checkbox checked={selectedValues.indexOf(item.key) > -1} />
            <ListItemText primary={item.value} />
          </>
        ) : (
          item.value
        )}
      </MenuItem>
    ))
    if (!multiple)
      options.unshift(
        <MenuItem key="all" value="">
          {defaultFilterValue}
        </MenuItem>
      )
    return options
  }

  const getRenderValue = (selectedValue) => {
    return (
      <span>
        {props.label}:
        <span className={classes.inlineValue}>
          {values.length ? (
            (!Array.isArray(selectedValue) && selectedValue !== '') ||
            (Array.isArray(selectedValue) && selectedValue.length) ? (
              multiple ? (
                selectedValues
                  .map((valueSelected) => values.find(({ key }) => key === valueSelected))
                  .filter((el) => el)
                  .map((el) => el.value)
                  .join(', ')
              ) : (
                values.find(({ key }) => key === selectedValue)?.value
              )
            ) : (
              defaultFilterValue
            )
          ) : (
            <CircularProgress size={14} />
          )}
        </span>
      </span>
    )
  }

  const onCloseProps = multiple
    ? { onClose: () => props.onChange({ target: { name: props.name, value: selectedValues } }) }
    : {}

  return (
    <FormControl
      variant="outlined"
      className={classes.root}
      required={props.required}
      disabled={props.disabled}
    >
      <Select
        autoWidth
        className={classes.input}
        name={props.name}
        value={multiple ? selectedValues : value}
        multiple={multiple}
        onChange={handleChange}
        {...onCloseProps}
        displayEmpty
        renderValue={getRenderValue}
        MenuProps={MenuProps}
      >
        {getSelectOptions()}
      </Select>
    </FormControl>
  )
})
